import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { useData } from '../../hooks/useData';
import { useUI } from '../../providers/MoleUIContext';
import { LocalStorageKeys } from '../../util/constants';
import type { ClientWalletRow } from './walletTypes';

const asRows = (payload: unknown): ClientWalletRow[] =>
  Array.isArray(payload) ? payload : (payload as { items?: ClientWalletRow[] })?.items || [];

export const useWalletDirectory = () => {
  const { api } = useAuth();
  const { queueReRun, t } = useUI();
  const {
    data,
    lastUpdate,
    loadFromIndexedDB,
    saveFromServer,
    loading,
    setLoading,
  } = useData<ClientWalletRow>(LocalStorageKeys.WALLET_LIST);
  const toastIdRef = useRef<string | number>(-1);
  const firstLoadRef = useRef(false);

  const fetchWallets = async (notify = true) => {
    setLoading(true);
    const res = await api.get('/wallets');
    if (res?.status === 200) {
      const rows = asRows(res.data);
      await saveFromServer(rows, undefined, true);
      if (notify) {
        toast.update(toastIdRef.current, {
          render: t('common:toast.success.refresh'),
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(() => fetchWallets(notify));
    } else if (notify) {
      toast.dismiss(toastIdRef.current);
    }
    setLoading(false);
    return res;
  };

  useEffect(() => {
    if (firstLoadRef.current) return;
    firstLoadRef.current = true;
    let cancelled = false;
    (async () => {
      const loaded = await loadFromIndexedDB();
      if (cancelled || loaded) return;
      void fetchWallets(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setWallets = (rows: ClientWalletRow[]) => {
    void saveFromServer(rows, undefined, true);
  };

  return {
    wallets: data,
    lastUpdate,
    loading,
    fetchWallets,
    setWallets,
    toastIdRef,
  };
};
